/*
 * The placeholder-salary conversation (GC-21532).
 *
 * The Home alert that fires when the salary of record is still our $10,000
 * placeholder offers two pills, and BOTH are answered client-side rather than by
 * the agent:
 *
 *   "Learn more"      → the approved help text, verbatim. It is the same string
 *                       the alert card puts behind its help icon, and it is copy
 *                       that has been signed off — an agent paraphrasing why we
 *                       default someone's salary, and what does and doesn't
 *                       persist, is not something to leave to chance.
 *   "Enter my income" → ask for the number, then write it to the account.
 *
 * That keeps the whole exchange deterministic and off the wire: nothing here
 * creates a chat, calls `agents/run/stream`, or persists a message. It sits in
 * the client-only turn list above the transcript (see ChatView's `leadingTurns`),
 * exactly where the FE-authored opening comment already lived.
 *
 * The copy below marked NEW is not in the story, which specifies only the help
 * text and the two pill labels. It is written to be plain and to carry the same
 * caveat the help text does, and it wants a copy review before release.
 */

export const SALARY_PILL_ENTER = 'Enter my income'
export const SALARY_PILL_LEARN_MORE = 'Learn more'

/*
 * The alert's starter pills. Defined here, with the behaviour they trigger,
 * rather than beside the other Home chat copy — a label that no longer matches
 * what `useSalaryPrompt` matches on is a pill that silently falls through to the
 * agent, which is the exact failure this module exists to prevent.
 */
export const SALARY_SUGGESTIONS = [SALARY_PILL_ENTER, SALARY_PILL_LEARN_MORE]

// The two paragraphs exactly as signed off. Kept as one string so the bubble
// renders them the way the tooltip does (ChatView bubbles are pre-wrap).
export const SALARY_HELP_TEXT = [
  'We don’t currently receive your salary information from your retirement plan provider. Since we need a salary amount to generate advice, we temporarily use $10,000 as a default value.',
  'If you enter your actual salary, we’ll use that information for your current session. However, when you sign in again, the value may return to $10,000 because your retirement plan provider remains the source of record and is not supplying a salary amount for us to save and refresh.',
].join('\n\n')

// NEW copy — see the header note.
export const SALARY_ASK =
  'Sure. What’s your annual income before taxes? You can type it as a number, like 85,000.'

// NEW copy. Repeats the help text's caveat rather than claiming the change
// sticks: the provider re-supplies $0 on the next refresh, and a confirmation
// that reads as permanent would be the same surprise this alert exists to stop.
export const salaryConfirmation = amount =>
  `Thanks — I’ve set your annual income before taxes to $${amount.toLocaleString()}, and your advice will use it from here.\n\nOne thing to know: it may go back to $10,000 the next time you sign in, because your retirement plan provider is still the source of record for that number.`

// NEW copy.
export const SALARY_RETRY =
  'Sorry, I didn’t catch a dollar amount there. What’s your annual income before taxes? For example, 85,000.'

// NEW copy. Sends the user somewhere that definitely works rather than looping.
export const SALARY_SAVE_FAILED =
  'Sorry — something went wrong saving that. You can try again here, or update it in your profile under “Your annual compensation before taxes”.'

/*
 * The profile's own bounds (AboutYouEdit's yup schema: greater than $0, less
 * than $10,000,000). Matched deliberately — a number this rejects has to be a
 * number the profile form would reject too, or the chat becomes a way to write a
 * salary the rest of the app considers invalid.
 */
export const MIN_SALARY = 1
export const MAX_SALARY = 9999999

// NEW copy, used when a number parses but falls outside those bounds — distinct
// from SALARY_RETRY so the user isn't told we couldn't read what we clearly did.
export const SALARY_OUT_OF_RANGE =
  'That doesn’t look like an annual salary I can use — it needs to be more than $0 and less than $10,000,000. What’s your annual income before taxes?'

/**
 * Pull an annual salary out of whatever the user typed.
 *
 * Deliberately forgiving about the shape of the answer ("$85,000", "85000",
 * "85k", "I make about 85,000 a year") because this is a chat box, not a
 * currency field — the user has just been asked a direct question and will
 * answer it in a sentence as often as not.
 *
 * @returns {number|null} the amount, or null when there is no number in `text`
 */
export const parseSalary = text => {
  if (typeof text !== 'string') return null

  // Drop currency symbols and thousands separators first, so "85,000" reads as
  // one number rather than as 85 followed by 000.
  const cleaned = text.replace(/[$,]/g, '')
  const match = cleaned.match(/(\d+(?:\.\d+)?)\s*(k)?\b/i)
  if (!match) return null

  const amount = Number(match[1]) * (match[2] ? 1000 : 1)
  if (!Number.isFinite(amount)) return null

  // The account stores whole dollars, and the profile field enters them that way.
  return Math.round(amount)
}

/** Whether a parsed amount is one the profile form would also accept. */
export const isSalaryInRange = amount =>
  typeof amount === 'number' && amount >= MIN_SALARY && amount <= MAX_SALARY
